import { effect, inject, Injectable, PLATFORM_ID, untracked } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MediaBlockService } from './media-block-service';
import { captureVideoFrame as captureVideoFrameFromVideo } from '../utils/video-capture';

const CAPTURE_TIMEOUT_MS: number = 5000;

@Injectable({
  providedIn: 'root',
})
export class VideoCaptureFrame {
  private readonly mediaBlockService: MediaBlockService = inject(MediaBlockService);
  private readonly isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly frameCache: Map<number, string | null> = new Map<number, string | null>();
  private queue: Promise<unknown> = Promise.resolve();
  private captureVideo: HTMLVideoElement | undefined;

  constructor() {
    // Invalide le cache dès qu'un nouveau fichier vidéo est chargé
    effect(() => {
      this.mediaBlockService.videoFile();
      this.frameCache.clear();
      if (this.isBrowser) untracked(() => this.releaseCaptureVideo());
    });
  }

  /**
   * Capture *(ou retourne depuis le cache)* l'image d'une vidéo à un timestamp donné.
   * Les frames sont mises en cache par timestamp pour éviter les re-captures
   * lorsque le composant est recréé *(ex : ouverture de la modale fullscreen)*.
   */
  /**
   * Retourne l'URL en cache de façon synchrone.
   * Retourne `undefined` si le timestamp n'a jamais été capturé (pas dans le cache).
   * Retourne `null` si la capture a échoué.
   */
  getCached(timestamp: number): string | null | undefined {
    return this.frameCache.get(timestamp);
  }

  async captureFrame(timestamp: number): Promise<string | null> {
    if (!this.isBrowser) return null;

    if (this.frameCache.has(timestamp)) {
      return this.frameCache.get(timestamp)!;
    }

    return this.enqueue(async () => {
      if (this.frameCache.has(timestamp)) return this.frameCache.get(timestamp)!;

      const video: HTMLVideoElement | undefined = this.getCaptureVideo();
      if (!video) return null;

      const url: string | null = await Promise.race([
        captureVideoFrameFromVideo(video, timestamp, { quality: 0.75, restoreTime: false }),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), CAPTURE_TIMEOUT_MS)),
      ]);
      // Vidéo changée pendant la capture : la frame ne correspond plus, on ne la met pas en cache.
      if (video !== this.captureVideo) return null;

      this.frameCache.set(timestamp, url);
      return url;
    });
  }

  /**
   * Capture ponctuelle hors cache (ex : miniature), sur la vidéo invisible : le lecteur visible ne bouge pas.
   */
  captureFrameUncached(timestamp: number, quality: number): Promise<string | null> {
    if (!this.isBrowser) return Promise.resolve(null);
    return this.enqueue(async () => {
      const video: HTMLVideoElement | undefined = this.getCaptureVideo();
      if (!video) return null;
      return Promise.race([
        captureVideoFrameFromVideo(video, timestamp, { quality, restoreTime: false }),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), CAPTURE_TIMEOUT_MS)),
      ]);
    });
  }

  /**
   * Élément vidéo invisible dédié aux captures, chargé avec le même fichier que le lecteur :
   * les seeks des captures ne font plus défiler la vidéo visible.
   */
  private getCaptureVideo(): HTMLVideoElement | undefined {
    const url: string | undefined = this.mediaBlockService.videoUrl();
    if (!url) return undefined;
    if (this.captureVideo?.src === url) return this.captureVideo;

    this.releaseCaptureVideo();
    const video: HTMLVideoElement = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('aria-hidden', 'true');
    // Attachée au DOM (certains navigateurs ne décodent pas une vidéo détachée), mais hors écran.
    video.style.cssText =
      'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
    video.src = url;
    document.body.appendChild(video);
    this.captureVideo = video;
    return video;
  }

  private releaseCaptureVideo(): void {
    if (!this.captureVideo) return;
    this.captureVideo.removeAttribute('src');
    this.captureVideo.load();
    this.captureVideo.remove();
    this.captureVideo = undefined;
  }

  /**
   * Exécute les tâches une par une : toutes les captures déplacent le même élément vidéo,
   * deux seeks simultanés captureraient la mauvaise frame.
   */
  private enqueue<T>(task: () => Promise<T>): Promise<T> {
    const run: Promise<T> = this.queue.then(task);
    this.queue = run.catch(() => undefined);
    return run;
  }
}
